<template>
  <div class="imagen-compresion-container">
    <div class="flex flex-row w-full justify-content-between align-items-center mb-4">
      <div class="flex flex-column">
        <h1 class="primaryFont title">Carga de imagen</h1>
        <p class="subtitle">Investigación técnica de reducción automática de peso de imágenes.</p>
      </div>
    </div>

    <div class="card-form">
      <h2 class="section-title">Procesar imagen</h2>

      <div class="grid">
        <div class="col-12 md:col-5">
          <label class="field-label">Imagen</label>
          <input
            type="file"
            accept="image/jpeg,image/png"
            class="file-input"
            @change="handleFileChange"
          />
        </div>

        <div class="col-12 md:col-3">
          <label class="field-label">Compresión</label>
          <Dropdown
            v-model="form.calidad"
            :options="calidadOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Seleccione calidad"
            class="w-full"
          />
        </div>

        <div class="col-12 md:col-3">
          <label class="field-label">Peso objetivo</label>
          <Dropdown
            v-model="form.pesoObjetivoMb"
            :options="pesoOptions"
            optionLabel="label"
            optionValue="value"
            placeholder="Seleccione peso"
            class="w-full"
          />
        </div>

        <div class="col-12 md:col-1 flex align-items-end">
          <Button
            label="Procesar"
            icon="pi pi-upload"
            class="procesar-btn w-full"
            :loading="loading"
            :disabled="!selectedFile || loading"
            @click="procesarImagen"
          />
        </div>
      </div>

      <Message v-if="error" severity="error" class="mt-3">
        {{ error }}
      </Message>

      <Message v-if="successMessage" severity="success" class="mt-3">
        {{ successMessage }}
      </Message>
    </div>

    <div v-if="resultado" class="card-result mt-4">
      <h2 class="section-title">Resultado de compresión</h2>

      <div class="result-grid">
        <div class="result-item">
          <span class="result-label">Peso original</span>
          <strong>{{ resultado.PesoOriginalMb }} MB</strong>
        </div>

        <div class="result-item">
          <span class="result-label">Peso optimizado</span>
          <strong>{{ resultado.PesoOptimizadoMb }} MB</strong>
        </div>

        <div class="result-item">
          <span class="result-label">Reducción</span>
          <strong>{{ resultado.PorcentajeReduccion }}%</strong>
        </div>

        <div class="result-item">
          <span class="result-label">Calidad aplicada</span>
          <strong>{{ resultado.CalidadAplicada }}</strong>
        </div>

        <div class="result-item">
          <span class="result-label">Objetivo</span>
          <strong>{{ resultado.PesoObjetivoMb }} MB</strong>
        </div>

        <div class="result-item">
          <span class="result-label">Estado</span>
          <strong :class="resultado.Estado === 'DENTRO_OBJETIVO' ? 'estado-ok' : 'estado-error'">
            {{ resultado.Estado }}
          </strong>
        </div>

        <div class="result-item">
          <span class="result-label">Resolución original</span>
          <strong>{{ resultado.AnchoOriginal }} x {{ resultado.AltoOriginal }}</strong>
        </div>

        <div class="result-item">
          <span class="result-label">Resolución optimizada</span>
          <strong>{{ resultado.AnchoOptimizado }} x {{ resultado.AltoOptimizado }}</strong>
        </div>

        <div class="result-item">
          <span class="result-label">Tiempo</span>
          <strong>{{ resultado.TiempoProcesamientoMs }} ms</strong>
        </div>
      </div>

      <div class="preview-grid mt-4">
        <div class="preview-card">
          <h3>Original</h3>
          <img
            v-if="resultado.UrlOriginal"
            :src="resultado.UrlOriginal"
            alt="Imagen original"
            class="preview-image"
          />
          <a
            v-if="resultado.UrlOriginal"
            :href="resultado.UrlOriginal"
            target="_blank"
            class="preview-link"
          >
            Abrir original
          </a>
        </div>

        <div class="preview-card">
          <h3>Optimizada</h3>
          <img
            v-if="resultado.UrlOptimizada"
            :src="resultado.UrlOptimizada"
            alt="Imagen optimizada"
            class="preview-image"
          />
          <a
            v-if="resultado.UrlOptimizada"
            :href="resultado.UrlOptimizada"
            target="_blank"
            class="preview-link"
          >
            Abrir optimizada
          </a>
        </div>
      </div>
    </div>

    <div class="card-table mt-4">
      <div class="flex justify-content-between align-items-center mb-3">
        <h2 class="section-title m-0">Últimas pruebas</h2>

        <Button
          label="Actualizar"
          icon="pi pi-refresh"
          class="actualizar-btn"
          :loading="loadingListado"
          @click="getPruebas"
        />
      </div>

      <DataTable
        :value="pruebas"
        :loading="loadingListado"
        responsiveLayout="scroll"
        stripedRows
        class="w-full"
      >
        <Column field="ImagenCompresionPruebaID" header="ID" />
        <Column field="PesoOriginalMb" header="Original MB" />
        <Column field="PesoOptimizadoMb" header="Final MB" />
        <Column field="PorcentajeReduccion" header="Reducción %" />
        <Column field="CalidadAplicada" header="Calidad" />
        <Column field="PesoObjetivoMb" header="Objetivo MB" />

        <Column header="Estado">
          <template #body="{ data }">
            <span :class="data.Estado === 'DENTRO_OBJETIVO' ? 'estado-ok' : 'estado-error'">
              {{ data.Estado }}
            </span>
          </template>
        </Column>

        <Column header="Original">
          <template #body="{ data }">
            <a v-if="data.UrlOriginal" :href="data.UrlOriginal" target="_blank"> Ver </a>
          </template>
        </Column>

        <Column header="Optimizada">
          <template #body="{ data }">
            <a v-if="data.UrlOptimizada" :href="data.UrlOptimizada" target="_blank"> Ver </a>
          </template>
        </Column>
      </DataTable>
    </div>
  </div>
</template>

<script setup>
import { onMounted, reactive, ref, computed } from "vue";
import { storeToRefs } from "pinia";
import { useImagenCompresionPruebaStore } from "@/store/Pinia/useImagenCompresionPruebaStore";

const imagenCompresionStore = useImagenCompresionPruebaStore();

const {
  pruebas,
  resultado,
  isLoading,
  isListLoading,
  error: storeError,
} = storeToRefs(imagenCompresionStore);

const calidadOptions = [
  { label: "85 - mejor calidad, menor compresión", value: 85 },
  { label: "75  ", value: 75 },
  { label: "65  ", value: 65 },
  { label: "55  ", value: 55 },
  { label: "45  ", value: 45 },
  { label: "35  ", value: 35 },
  { label: "25  ", value: 25 },
  { label: "15  ", value: 15 },
  { label: "5 - menor calidad, Mayor compresión", value: 5 },
];

const pesoOptions = [
  { label: "5 MB", value: 5 },
  { label: "10 MB", value: 10 },
  { label: "15 MB", value: 15 },
];

const form = reactive({
  calidad: 75,
  pesoObjetivoMb: 5,
});

const selectedFile = ref(null);
const localError = ref("");
const successMessage = ref("");

const loading = computed(() => isLoading.value);
const loadingListado = computed(() => isListLoading.value);

const error = computed(() => {
  return localError.value || storeError.value || "";
});

const handleFileChange = (event) => {
  const file = event.target.files && event.target.files[0] ? event.target.files[0] : null;

  selectedFile.value = file;
  localError.value = "";
  successMessage.value = "";

  imagenCompresionStore.clearResultado();
};

const procesarImagen = async () => {
  if (!selectedFile.value) {
    localError.value = "Debe seleccionar una imagen.";
    return;
  }

  localError.value = "";
  successMessage.value = "";

  const response = await imagenCompresionStore.procesarImagen({
    imagen: selectedFile.value,
    calidad: form.calidad,
    pesoObjetivoMb: form.pesoObjetivoMb,
  });

  if (response) {
    successMessage.value = "Imagen procesada correctamente.";
  }
};

const getPruebas = async () => {
  localError.value = "";
  await imagenCompresionStore.getPruebas(20);
};

onMounted(async () => {
  await getPruebas();
});
</script>

<style scoped>
.imagen-compresion-container {
  width: 100%;
  padding: 0 2rem;
}

.title {
  font-weight: 700;
  font-size: 28px;
  line-height: 32px;
  color: #000000;
}

.subtitle {
  margin-top: 0.5rem;
  font-weight: 400;
  font-size: 15px;
  color: #475569;
}

.card-form,
.card-result,
.card-table {
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 1.25rem;
}

.section-title {
  font-weight: 600;
  font-size: 20px;
  margin-bottom: 1rem;
  color: #111827;
}

.field-label {
  display: block;
  font-weight: 500;
  margin-bottom: 0.5rem;
  color: #374151;
}

.file-input {
  width: 100%;
  padding: 0.6rem;
  border: 1px solid #d1d5db;
  border-radius: 6px;
  background: #ffffff;
}

.procesar-btn,
.actualizar-btn {
  background-color: #0b5195;
  border: 1px solid #0b5195;
  color: #ffffff;
}

.procesar-btn:hover,
.actualizar-btn:hover {
  background-color: #0b5195;
  opacity: 0.9;
}

.result-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 1rem;
}

.result-item {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 0.75rem;
  background: #f8fafc;
}

.result-label {
  display: block;
  font-size: 13px;
  color: #64748b;
  margin-bottom: 0.25rem;
}

.estado-ok {
  color: #15803d;
  font-weight: 700;
}

.estado-error {
  color: #b91c1c;
  font-weight: 700;
}

.preview-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 1rem;
}

.preview-card {
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 1rem;
}

.preview-card h3 {
  margin-top: 0;
  font-size: 16px;
  font-weight: 600;
}

.preview-image {
  width: 100%;
  max-height: 420px;
  object-fit: contain;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  background: #f8fafc;
}

.preview-link {
  display: inline-block;
  margin-top: 0.75rem;
  color: #0b5195;
  font-weight: 500;
}

::v-deep .p-datatable .p-datatable-thead > tr > th {
  font-weight: 600;
}

@media (max-width: 768px) {
  .result-grid,
  .preview-grid {
    grid-template-columns: 1fr;
  }
}
</style>
